import {action, observable, runInAction} from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import {GoogleMap, Marker} from '@react-google-maps/api';
import { bind } from '../Utils/Bind';
import { LoadGoogleMapScript } from './LoadGoogleMapScript';
import {Dropdown, DropdownItem, DropdownMenu, DropdownToggle} from 'reactstrap';

const mapStyles = require('../Styles/mapStyles.json'); // eslint-disable-line

export interface IMapRef {
    mapRef?(ref: google.maps.Map | null): void;
}

export interface IMapProps {
    mapRef?(ref: google.maps.Map | null): void;
    onLoaded?(): void;
    onZoom?(zoom: number): void;
    readonly dark?: boolean;
    readonly defaultCenter?: { lat: number; lng: number };
    readonly zoom?: number;
    readonly height?: string;
    readonly disableRefitBounds?: boolean;
    readonly recenter?: boolean;
    readonly latLngs?: google.maps.LatLngLiteral[];
    readonly style?: React.CSSProperties;
    readonly mapOptionsChildren?: React.ReactNode;
}

@observer
export class Map extends React.Component<IMapProps> {
    public render() {
        const { zoom } = this.props;

        return (
            <div style={{position: 'relative'}} className="h-100 w-100">
                <LoadGoogleMapScript>
                    <GoogleMap
                        mapContainerStyle={{height: this.props.height ?? '375px', width: '100%', ...this.props.style}}
                        center={this.center}
                        options={{styles: this.props.dark ? mapStyles : ''}}
                        zoom={zoom || 14}
                        onLoad={this.onLoad}
                        onTilesLoaded={this.props.onLoaded}
                        onZoomChanged={this.onZoomChange}
                    >
                        {this.props.children}
                        {this.userLocation && (
                            <Marker
                                position={this.userLocation}
                                icon={{
                                    path: google.maps.SymbolPath.CIRCLE,
                                    scale: 8,
                                    fillColor: '#4285F4',
                                    fillOpacity: 1,
                                    strokeWeight: 2,
                                    strokeColor: 'white',
                                }}
                            />
                        )}
                    </GoogleMap>
                </LoadGoogleMapScript>

                <div style={{position: 'absolute', top: 10, right: 10, zIndex: 5}}>
                    <Dropdown isOpen={this.dropdownOpen} toggle={this.toggleDropdown}>
                        <DropdownToggle caret color="light">
                            Map Options
                        </DropdownToggle>
                        <DropdownMenu>
                            <DropdownItem onClick={this.centerOnUserLocation}>📍 My Location</DropdownItem>
                            {this.props.latLngs && this.props.latLngs?.length > 0 && (
                                <DropdownItem onClick={() => this.refit(this.props.latLngs)}>Show Displays</DropdownItem>
                            )}
                            {this.props.mapOptionsChildren}
                        </DropdownMenu>
                    </Dropdown>
                </div>

                {this.isLocating && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            background: 'rgba(255, 255, 255, 0.9)',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                            zIndex: 10,
                        }}
                    >
                        📍 Getting Location...
                    </div>
                )}

            </div>

        );
    }

    @bind
    @action
    private onZoomChange() {
        let zoom = this.map?.getZoom();
        if (zoom && this.props.onZoom) this.props.onZoom(zoom);
    }

    @bind
    @action
    private onLoad(r: google.maps.Map | null) {
        this.map = r;
        if (this.props.mapRef) this.props.mapRef(r);
        if (this.props.defaultCenter) {
            this.center = this.props.defaultCenter;
        } else {
            this.centerOnUserLocation(); // Center on user if no default
        }
    }

    @bind
    @action
    private centerOnUserLocation() {
        this.isLocating = true;

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    runInAction(() => {
                        const location = {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                        };
                        this.center = location;
                        this.userLocation = location;
                        this.map?.panTo(this.center);
                        this.isLocating = false;
                    });
                },
                (error) => {
                    console.error('Error getting user location:', error);
                    runInAction(() => {
                        this.isLocating = false;
                    });
                }
            );
        } else {
            console.warn('Geolocation is not supported by this browser.');
            this.isLocating = false;
        }
    }

    @bind
    @action
    private refit(latLngs?: google.maps.LatLngLiteral[]) {
        if (latLngs && this.latLngsLength != latLngs.length) {
            this.recenter(latLngs[0]);
        }

        this.latLngsLength = latLngs?.length || 0;

        if (this.map && !this.props.disableRefitBounds && latLngs) {
            const bounds = new google.maps.LatLngBounds(
                { lat: 40.69925, lng: -74.028551 },
                { lat: 40.717338, lng: -73.991006 }
            );

            for (const latLng of latLngs) {
                if (!bounds.contains(latLng)) {
                    bounds.extend(latLng);
                }
            }
            this.refitBounds = false;
            this.map.fitBounds(bounds);
        }
    }

    @bind
    @action
    private toggleDropdown() {
        this.dropdownOpen = !this.dropdownOpen;
    }
    
    @bind
    private recenter(center?: { lat: number; lng: number }) {
        if (center && this.center != center) {
            this.map?.panTo(center);
            this.center = center;
        } else if (this.props.latLngs?.length) this.map?.panTo(this.props.latLngs[0]);
    }


    @observable
    private isLocating: boolean = false;
    @observable
    private dropdownOpen: boolean = false;
    @observable
    private refitBounds = true;
    @observable
    private latLngsLength = 0;
    @observable
    private center: google.maps.LatLngLiteral = {lat: 40.7102563, lng: -74.01568};
    @observable
    private userLocation: google.maps.LatLngLiteral | null = null;
    @observable
    private map: google.maps.Map | null | null = null;
}

export async function geocodeLatLng(lat: number, lng: number) {
    try {
        const response = await fetch(`/api/v1/google-maps/geocode?lat=${lat}&lng=${lng}`);
        const data = await response.json();

        if (data.status === 'OK') {
            return data.results[0];
        } else {
            console.warn('Geocoding failed:', data.status);
        }
    } catch (e) {
        console.error('Geocoding error:', e);
    }
}